/**
 * Trade / generic equivalency groups for perioperative meds.
 * Kept aligned with server/app/services/algorithm/rule_definitions.py
 * (e.g. APIXABAN_SCHEDULE, warfarin list, spinal cord stimulator has_medication checks).
 *
 * Within each tuple, index 0 is the preferred generic / display INN-style key.
 */

export const MEDICATION_EQUIVALENCE_GROUPS = [
  ['APIXABAN', 'ELIQUIS'],
  ['RIVAROXABAN', 'XARELTO', 'CERELTO'],
  ['EDOXABAN', 'LIXIANA', 'SAVAYSA'],
  ['DABIGATRAN_ETEXILATE', 'DABIGATRAN', 'PRADAXA'],
  ['WARFARIN', 'COUMADIN', 'JANTOVIN'],
  ['CLOPIDOGREL', 'PLAVIX'],
  ['PRASUGREL', 'EFFIENT'],
  ['TICAGRELOR', 'BRILINTA'],
  ['ASPIRIN', 'BAYER'],
];

const FORMAT_KEY_REGEX = /\s+/g;

/** Build map: UPPERCASE_UNDER_SCORE_ALIAS -> canonical UPPERCASE_UNDER_SCORE */
function buildUpperToCanonicalMap() {
  const map = new Map();
  for (const group of MEDICATION_EQUIVALENCE_GROUPS) {
    const canonical = group[0].toUpperCase();
    for (const name of group) {
      map.set(name.toUpperCase(), canonical);
    }
  }
  return map;
}

const UPPER_TO_CANONICAL = buildUpperToCanonicalMap();

/**
 * Format like MedicationCheckboxForm (uppercase + spaces -> underscores).
 * @param {string} key
 * @returns {string}
 */
export function formatMedicationKey(key) {
  if (typeof key !== 'string' || !key) return '';
  return key.toUpperCase().replace(FORMAT_KEY_REGEX, '_');
}

/**
 * Map a formatted medication code to canonical generic code (same casing style as MEDICATION_EQUIVALENCE_GROUPS[0]).
 * @param {string} formattedKey — e.g. from formatMedicationKey
 * @returns {string}
 */
export function canonicalMedicationKey(formattedKey) {
  const u = String(formattedKey).toUpperCase();
  return UPPER_TO_CANONICAL.get(u) || u;
}

function mergeMedicationDetails(preferred, fallback) {
  return { ...(fallback || {}), ...(preferred || {}) };
}

function resolveEquivalence(rawMedicationKey) {
  const upper =
    typeof rawMedicationKey === 'string'
      ? rawMedicationKey.toUpperCase().replace(FORMAT_KEY_REGEX, '_')
      : '';
  for (const group of MEDICATION_EQUIVALENCE_GROUPS) {
    if (group.some((g) => g.toUpperCase() === upper)) {
      return { mergeKey: group[0].toUpperCase(), displayKey: group[0] };
    }
  }

  return { mergeKey: upper, displayKey: rawMedicationKey };
}

/**
 * Merge duplicate trade+generic entries; keys use the generic name from each group.
 * Preserves first-seen order of merged rows.
 * @param {Record<string, object>|null|undefined} medications
 * @returns {Record<string, object>}
 */
export function mergeEquivalentMedicationsForDisplay(medications) {
  if (!medications || typeof medications !== 'object') return {};

  const buckets = new Map();
  /** @type {string[]} mergeKey order */
  const order = [];

  for (const [rawKey, details] of Object.entries(medications)) {
    const { mergeKey, displayKey } = resolveEquivalence(rawKey);
    const rawUpper = formatMedicationKey(rawKey);

    if (!buckets.has(mergeKey)) {
      order.push(mergeKey);
      buckets.set(mergeKey, { displayKey, details: { ...details } });
      continue;
    }

    const existing = buckets.get(mergeKey);
    const canonicalDetails =
      rawUpper === mergeKey ? mergeMedicationDetails(details, existing.details) : mergeMedicationDetails(existing.details, details);
    buckets.set(mergeKey, { ...existing, details: canonicalDetails });
  }

  const out = {};
  for (const k of order) {
    const { displayKey, details } = buckets.get(k);
    out[displayKey] = details;
  }
  return out;
}
